/**
 * GET /me, the one read every Operator page and the sidebar share.
 *
 * `useOperatorReach` is what the sidebar, the rail and the palette offer
 * (`OperatorReach` in access-model.ts): the rows, the admin screens, and Talk.
 * It asks GET /me for everybody, because only the service knows who has a
 * seat and with which role (an admin here may be a member there). An admin
 * here is drawn the rows while the answer is on its way, never the admin
 * screens. Talk's demo switch
 * (GET /talk/access) is read slowly, and only for a person with a seat who is
 * not the power admin, for whom Talk shows only while the demo is open.
 */
import { useQuery } from '@tanstack/react-query'
import { useSession } from '../../../lib/session'
import { operatorApi, operatorPoll, operatorQueryKeys, operatorRetry } from './api'
import { accessFrom, operatorReachOf, talkIsOpen, type OperatorReach } from './access-model'
import type { OperatorMe } from './types'

export function useOperatorMe(enabled = true) {
  return useQuery({
    queryKey: operatorQueryKeys.me(),
    queryFn: ({ signal }) => operatorApi<OperatorMe>('/me', { signal }),
    staleTime: 60_000,
    retry: operatorRetry,
    enabled,
  })
}

/** How often the sidebar reads Talk's demo switch, for a person it decides Talk for. */
export const TALK_ACCESS_POLL_MS = 5 * 60_000

export function useOperatorReach(): OperatorReach {
  const { isAdmin, session } = useSession()
  const me = useQuery({
    queryKey: operatorQueryKeys.me(),
    queryFn: ({ signal }) => operatorApi<OperatorMe>('/me', { signal }),
    enabled: session !== undefined,
    staleTime: 5 * 60_000,
    retry: false,
  })
  const access = accessFrom(me)
  const asksDemo = access.state === 'allowed' && !access.me.user.isPowerAdmin
  const demo = useQuery({
    queryKey: operatorQueryKeys.talkAccess(),
    queryFn: ({ signal }) => operatorApi<unknown>('/talk/access', { signal }),
    enabled: session !== undefined && asksDemo,
    staleTime: 60_000,
    refetchInterval: operatorPoll(asksDemo ? TALK_ACCESS_POLL_MS : false),
    retry: false,
  })
  return operatorReachOf(isAdmin, access, asksDemo && talkIsOpen(demo.data, Date.now()))
}
